import React, { useState } from 'react';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
  SettingsRow,
} from '../SettingsShell/SettingsPage';
import { SetInput, SetSegmented, SetSlider, SetSwitch } from '../SettingsShell/controls';
import type { CancellationPolicy } from '../../settings-cancel/types';

export interface CancellationPolicyViewProps {
  value: CancellationPolicy;
  location: string;
  onChange?: (next: CancellationPolicy) => void;
}

/** `2 attempts to reschedule`, not `2` (§5.1). The slider reads out in words. */
function attemptsReadOut(n: number): string {
  if (n === 0) return 'Accept the cancellation straight away';
  if (n === 1) return '1 attempt to reschedule';
  return `${String(n)} attempts to reschedule`;
}

/**
 * Appointment & booking › Cancellation policy (spec 16 §5). Three cards and a
 * chain note.
 *
 * The page sits in the middle of a chain it does not own either end of, which is
 * why it is short: it holds the four sentences a practice has an opinion about —
 * how much notice, how much it costs, how it is said, and how hard to try first —
 * and hands the rest on.
 */
export function CancellationPolicyView({ value, location, onChange }: CancellationPolicyViewProps) {
  const [policy, setPolicy] = useState(value);
  const set = <K extends keyof CancellationPolicy>(key: K, v: CancellationPolicy[K]) => {
    const next = { ...policy, [key]: v };
    setPolicy(next);
    onChange?.(next);
  };
  const feeDollars = String(Math.round(policy.lateFeeCents / 100));

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Settings · Appointment & booking"
        titleLead="Cancellation"
        titleAccent="policy"
        sub={
          <>
            What ELVA says when a patient wants to cancel, and what happens to the freed time.
          </>
        }
      />

      <SettingsCards>
        {/* 01 — the policy itself. A practice policy, not an agent's. */}
        <SettingsCard
          n="01"
          title="The policy"
          sub={
            <>
              What ELVA tells patients, on every channel.
            </>
          }
        >
          <SettingsRow
            label="Notice a patient must give"
            desc="Cancel with less warning than this and the visit counts as late."
            control={
              <SetInput
                ariaLabel="Notice a patient must give, in hours"
                className="w-[130px]"
                value={String(policy.noticeHours)}
                onChange={(v) => { set('noticeHours', Number(v.replace(/\D/g, '')) || 0); }}
              />
            }
          />
          <SettingsRow
            label="The late fee"
            desc="The fee ELVA quotes for a late cancellation."
            control={
              <SetInput
                ariaLabel="The late fee, in dollars"
                prefix="$"
                className="w-[120px]"
                value={feeDollars}
                onChange={(v) => { set('lateFeeCents', (Number(v.replace(/\D/g, '')) || 0) * 100); }}
              />
            }
          />
          <SettingsRow
            last
            label="How it is said"
            desc={
              <>
                <b className="font-semibold text-[var(--text-secondary)]">Soft guilt</b> reminds the patient of the
                policy and stops. <b className="font-semibold text-[var(--text-secondary)]">Strict</b> quotes the amount.
              </>
            }
            control={
              <SetSegmented
                ariaLabel="How it is said"
                value={policy.tone}
                options={[
                  { value: 'soft', label: 'Soft guilt' },
                  { value: 'strict', label: 'Strict policy' },
                ]}
                onChange={(v) => { set('tone', v); }}
              />
            }
          />
          {/* The boundary. This is the only place it is stated. */}
          <SettingsNote tone="indigo">
            ELVA never charges the fee. Your team decides whether to apply it.
          </SettingsNote>
        </SettingsCard>

        {/* 02 — what ELVA does before it agrees. */}
        <SettingsCard
          n="02"
          title="When a patient wants to cancel"
          sub={
            <>
              What ELVA tries before it accepts a cancellation.
            </>
          }
        >
          <SettingsRow
            label="How hard ELVA tries to reschedule"
            desc="Offers another time before accepting. It stops after the patient says no twice."
          >
            <SetSlider
              ariaLabel="How hard ELVA tries to reschedule"
              value={policy.rescheduleAttempts}
              min={0}
              max={5}
              minLabel="Don’t try"
              maxLabel="Try 5×"
              readOut={attemptsReadOut}
              onChange={(n) => { set('rescheduleAttempts', n); }}
            />
          </SettingsRow>
          <SettingsRow
            label="Offer the next available slot instead"
            desc="When the patient is cancelling because the time does not suit rather than because they do not want the treatment."
            control={
              <SetSwitch
                label="Offer the next available slot instead"
                checked={policy.offerNextSlot}
                onChange={(v) => { set('offerNextSlot', v); }}
              />
            }
          />
          <SettingsRow
            last
            label="Never quote a fee to a patient in pain"
            desc="An emergency visit, or anything marked urgent."
            control={
              <SetSwitch
                label="Never quote a fee to a patient in pain"
                checked={policy.neverQuoteFeeInPain}
                onChange={(v) => { set('neverQuoteFeeInPain', v); }}
              />
            }
          />
          {/* Out of scope, and the page says so (§5.3). */}
          <SettingsNote>
            No-shows are handled by Recall, not here.
          </SettingsNote>
        </SettingsCard>

        {/* 03 — the card that pays for the page (§5.2). */}
        <SettingsCard
          n="03"
          accent="green"
          title="What happens to the empty slot"
          sub="What happens to the freed time."
        >
          <SettingsRow
            label="Hand the slot to Gap Filler"
            desc="When a cancellation is confirmed, Gap Filler starts looking for someone to take the time."
            control={
              <SetSwitch
                label="Hand the slot to Gap Filler"
                checked={policy.handToGapFiller}
                onChange={(v) => { set('handToGapFiller', v); }}
              />
            }
          />
          <SettingsRow
            last
            label="How long before it goes"
            desc="A short hold, in case the patient changes their mind."
            control={
              <SetInput
                ariaLabel="How long to hold the slot, in minutes"
                className="w-[140px]"
                value={String(policy.holdMinutes)}
                onChange={(v) => { set('holdMinutes', Number(v.replace(/\D/g, '')) || 0); }}
              />
            }
          />
        </SettingsCard>

        {/* The chain note — the page's own explanation of why it is short. */}
        <div className="rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] px-6 py-5">
          <div className="text-[13px] font-bold text-[var(--text-primary)]">
            One cancellation, three agents, one policy
          </div>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Link tone="#4C5BD4">AI Receptionist takes the call</Link>
            <Arrow />
            <Link tone="#8A6415">This policy decides what is said</Link>
            <Arrow />
            <Link tone="#1F7A56">Gap Filler sells the hour again</Link>
          </div>
        </div>
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            {policy.noticeHours} hours&rsquo; notice · ${feeDollars} ·{' '}
            {policy.tone === 'soft' ? 'soft guilt' : 'strict policy'} · {attemptsReadOut(policy.rescheduleAttempts).toLowerCase()} ·{' '}
            {policy.handToGapFiller
              ? `slot released to Gap Filler after ${String(policy.holdMinutes)} minutes`
              : 'the slot is not handed to Gap Filler'}
          </>
        }
      />
    </div>
  );
}

function Link({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span
      className="rounded-full px-2.5 py-1 text-[12px] font-bold"
      style={{ background: `${tone}14`, color: tone }}
    >
      {children}
    </span>
  );
}

function Arrow() {
  return <span className="text-[12px] text-[var(--text-muted)]">→</span>;
}
